import { t } from "i18next";
import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../../hooks/useFetch";
import { SelectOption, Sensor, TriggerType } from "../../../../../../types/types";
import { Label } from "../../../../../../ui/Label/Label";
import { SelectButton } from "../../../../../../ui/SelectButton/SelectButton";
import { ScrollArea } from "../../../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../../../ui/Button";
import { Input } from "../../../../../../ui/Input/Input";
import { useAppStore } from "../../../../../../stores/RootStore";
import { equipmentStatuses, triggerTypes } from "../../../../../../enums";
import DeleteTrigger from "../../DeleteTrigger";
import { Switch, SwitchSM } from "../../../../../../ui/Switch/Switch";
import { RequestDataType } from "../../AddTrigger";
import Spinner from "../../../../../../ui/Spinner/Spinner";
import { TriggersContext } from "../../Triggers";

type Props = {
  id: string | undefined;
  currentTrigger: TriggerType | undefined;
  closeEditModal: Function;
};

export const toTempType = (type: string) => {
  switch (type) {
    case "Sites::Triggers::RfidIn":
      return "rfid_in";
    case "Sites::Triggers::RfidOut":
      return "rfid_out";
    case "Sites::Triggers::RfidSwitch":
      return "rfid_switch";
    case "Sites::Triggers::ValueLessThan":
      return "value_less_than";
    case "Sites::Triggers::ValueMoreThan":
      return "value_more_than";
    default:
      return "";
  }
};

const ScreenSettingsTab = ({ id, currentTrigger, closeEditModal }: Props) => {
  const store = useAppStore()
  const equipments = useAppStore((store) => store.equipments);
  const { triggers, setTriggers } = useContext(TriggersContext)!;
  const statusChanged = equipmentStatuses.map((status) => {
    return { value: status, label: t(status) };
  });
  const statusChangedToList = [{ value: "", label: t("do_not_change_status") }].concat(statusChanged);

  const equipmentsWithDevice = equipments?.filter((item) => item.attributes.with_sensors === true)!;

  const [sensorList, setSensorList] = useState<SelectOption[]>();

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      name: currentTrigger?.attributes.name,
      type: toTempType(currentTrigger?.attributes.type!),
      enabled: currentTrigger?.attributes.enabled,
      min_triggered_period: currentTrigger?.attributes.min_triggered_period,
      sensor_id: currentTrigger?.attributes.sensor_id,
      status_change_to: currentTrigger?.attributes.status_change_to,
      value_more_than: currentTrigger?.attributes.value_more_than ? currentTrigger?.attributes.value_more_than : "",
      value_less_than: currentTrigger?.attributes.value_less_than ? currentTrigger?.attributes.value_less_than : "",
      card: currentTrigger?.attributes.string_value_equal ? currentTrigger?.attributes.string_value_equal : "",
      equipment: equipmentsWithDevice.find((eq) => {
        for (let el of eq.attributes.sensor_ids) {
          if (el == currentTrigger?.attributes.sensor_id) return true;
        }
        return false;
      })?.id,
      sensor: currentTrigger?.attributes.sensor_id ? currentTrigger?.attributes.sensor_id : null,
    },
  });

  const [{ response, isLoading, error }, doFetchSensors] = useFetch(
    `/directories/equipment_items/${watch("equipment")}/sensors`
  );

  useEffect(() => {
    const chosenEquipmentId = watch("equipment");
    if (!chosenEquipmentId) return;
    doFetchSensors();
  }, [watch("equipment")]);

  useEffect(() => {
    if (!response) return;
    const responseData = response.data as unknown as Sensor[];
    setSensorList(
      responseData.map((sensor) => {
        return { value: String(sensor.id), label: String(sensor.attributes.sensor_type.long_name) };
      })
    );
  }, [response]);

  const isTypeIs = (type: "rfid" | "value_less" | "value_more") => {
    const typeValue = watch("type");
    switch (type) {
      case "rfid":
        const isRfid = typeValue == "rfid_in" || typeValue == "rfid_out" || typeValue == "rfid_switch";
        return isRfid;

      case "value_less":
        const isValueLess = typeValue == "value_less_than";
        return isValueLess;

      case "value_more":
        const isValueMore = typeValue == "value_more_than";
        return isValueMore;
    }
  };

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);

  }

  // Обновление информации о триггере
  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/directories/triggers/${id}`
  );

  let requestData: RequestDataType;
  const onSubmit = (formData: any) => {
    requestData = {
      name: formData.name,
      min_triggered_period: Number(formData.min_triggered_period),
      enabled: formData.enabled,
      sensor_id: Number(formData.sensor),
      status_change_to: formData.status_change_to,
    };

    switch (formData.type) {
      //@ts-ignore
      case "rfid_switch":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidSwitch";
      //@ts-ignore
      case "rfid_out":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidOut";
      case "rfid_in":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidIn";
        requestData.string_value_equal = formData.card;
        break;
      case "value_less_than":
        requestData.type = "Sites::Triggers::ValueLessThan";
        requestData.value_less_than = Number(formData.value_less_than);
        break;
      case "value_more_than":
        requestData.type = "Sites::Triggers::ValueMoreThan";
        requestData.value_more_than = Number(formData.value_more_than);
        break;
    }

    doFetchItemSave({
      method: "PUT",
      data: {
        data: requestData,
      },
    });
  };

  const editTrigger = (itemSaveResponse: { data: unknown }) => {
    const newScreenData = itemSaveResponse.data as unknown as TriggerType;
    const withoutCurrentItem = triggers?.filter((trigger) => trigger.id != currentTrigger?.id)!;
    let newTriggers = [...withoutCurrentItem, newScreenData]
    store.setTriggers(newTriggers)
    setTriggers(newTriggers);
  };

  useEffect(() => {
    if (!saveResponse) return;
    editTrigger(saveResponse);
    closeEditModal();
  }, [saveResponse]);

  return (
    <>
      <ScrollArea className="w-full">
        <div className="flex flex-col w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col relative space-y-1">
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <div className="flex justify-between items-center py-2">
                        <Label htmlFor="enabled">{field.value ? <>{t("current_trigger_state")} - {t("trigger_enabled")}</> : <>{t("current_trigger_state")} - {t("trigger_disabled")}</>}</Label>
                        <SwitchSM checked={field.value} onCheckedChange={field.onChange} id="enabled" />
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-1.5">
                      <Input {...field} label={`${t("trigger_name")}*`} />
                    </div>
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <div className="relative space-y-2">
                      <Label>{t("trigger_type")}*</Label>
                      <SelectButton
                        disabled={true}
                        label={"Выберите тип триггера"}
                        searchable={false}
                        options={triggerTypes.filter(tt => tt.group === "equipment")}
                        value={value ? triggerTypes?.filter(tt => tt.group === "equipment").find((s) => s.value == String(value))! : null}
                        onChange={(e) => onChange(e?.value)}
                      />
                    </div>
                  )}
                />
                <Controller
                  name="min_triggered_period"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input type="number" {...field} label={`${t("min_triggered_period")}*`} />
                    </div>
                  )}
                />

                {sensorList ? (
                  <Controller
                    name="sensor"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                      <div className="relative space-y-2">
                        <Label>{t("sensor")}*</Label>
                        <SelectButton
                          disabled={watch("equipment") ? false : true}
                          label={"Выберите датчик"}
                          searchable={false}
                          options={sensorList}
                          value={value ? sensorList?.find((s) => s.value == String(value))! : null}
                          onChange={(e) => onChange(e?.value)}
                        />
                      </div>
                    )}
                  />
                ) : (
                  <div className="relative space-y-2">
                    <Label>{t("sensor")}*</Label>
                    <Spinner />
                  </div>
                )}
                {isTypeIs("value_more") && (
                  <Controller
                    name="value_more_than"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <div className="relative space-y-2">
                        <Input type="number" {...field} label={`${t("value_more_than")}*`} />
                      </div>
                    )}
                  />
                )}
                {isTypeIs("value_less") && (
                  <Controller
                    name="value_less_than"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <div className="relative space-y-2">
                        <Input type="number" {...field} label={`${t("value_less_than")}*`} />
                      </div>
                    )}
                  />
                )}
                {isTypeIs("rfid") && (
                  <Controller
                    name="card"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <div className="relative space-y-2">
                        <Input {...field} label={`${t("card")}*`} />
                      </div>
                    )}
                  />
                )}
                <Controller
                  name="status_change_to"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <div className="relative space-y-2">
                      <Label>{t("status_change_to")}*</Label>
                      <SelectButton
                        label={t("do_not_change_status")}
                        searchable={false}
                        options={statusChangedToList}
                        value={value ? statusChangedToList?.find((s) => s.value == String(value))! : null}
                        onChange={(e) => onChange(e?.value)}
                      />
                    </div>
                  )}
                />
              </div>

              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </ScrollArea>
      <DeleteTrigger
        id={id!}
        isDeleteOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        closeEditModal={closeEditModal}
      />
    </>
  );
};

export default ScreenSettingsTab;
